import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import seikurallogo from '../../assests/images/seikurallogo.png';
import ServicesDropdown from './dropdowns/ServicesDropdown';
import SolutionsDropdown from './dropdowns/SolutionsDropdown';
// import IndustriesDropdown from './dropdowns/IndustriesDropdown';
import ScrollProgressBar from './ScrollProgressBar';
import './Navbar.css';

const NAV_ITEMS = [
  { label: 'Home', to: '/', type: 'link', end: true },
  { label: 'About', to: '/about', type: 'link', end: false },
  { label: 'Services', to: '/services', type: 'dropdown', megaKey: 'services' },
  // { label: 'Solutions', to: '/solutions', type: 'dropdown', megaKey: 'solutions' },
  // Temporarily keep Industries as a direct link; its dropdown is disabled.
  { label: 'Industries', to: '/industries', type: 'link', end: false },
  { label: 'Careers', to: '/careers', type: 'link', end: false },
];

const MOBILE_SUBMENU_ITEMS = {
  services: [
    { title: 'Web & Custom App Development', to: '/services/web-development' },
    { title: 'Business Software Solutions', to: '/services/business-software' },
    { title: 'UI/UX & Product Design', to: '/services/ui-ux-design' },
    { title: 'Cloud & DevOps', to: '/services/cloud-devops' },
    { title: 'Digital Marketing', to: '/services/digital-marketing' },
  ],
  solutions: [
    { title: 'ERP Solutions', to: '/solutions#sol-erp' },
    { title: 'HRM Solutions', to: '/solutions#sol-hrm' },
    { title: 'CRM Solutions', to: '/solutions#sol-crm' },
    { title: 'LMS Solutions', to: '/solutions#sol-lms' },
    { title: 'Custom Business Applications', to: '/solutions#sol-custom' },
  ],
  industries: [
    { title: 'Healthcare & MedTech', to: '/industries/healthcare' },
    { title: 'E-commerce & Retail', to: '/industries/retail' },
    { title: 'Logistics & Supply Chain', to: '/industries/logistics' },
    { title: 'Education & EdTech', to: '/industries/education' },
    { title: 'Banking & Fintech', to: '/industries/fintech' },
    { title: 'Manufacturing & Industry 4.0', to: '/industries/manufacturing' },
  ],
};

const Navbar = () => {
  const [activeMega, setActiveMega] = useState(null); // 'services' | 'solutions' | 'industries' | null
  const [mobileSubmenu, setMobileSubmenu] = useState(null);

  const collapseRef = useRef(null);
  const megaTimeoutRef = useRef(null);
  const location = useLocation();

  /* ── On route change: close dropdowns ── */
  useEffect(() => {
    setActiveMega(null);
    setMobileSubmenu(null);
  }, [location.pathname, location.hash]);

  const closeMenu = () => {
    setActiveMega(null);
    setMobileSubmenu(null);
    window.scrollTo(0, 0);
    const el = collapseRef.current;
    if (el?.classList.contains('show')) {
      const bs = window.bootstrap?.Collapse?.getInstance(el);
      bs ? bs.hide() : el.classList.remove('show');
    }
  };

  /* ── Hover handlers for Mega Dropdowns ── */
  const handleMouseEnterLink = (e, megaKey) => {
    if (megaTimeoutRef.current) clearTimeout(megaTimeoutRef.current);
    if (window.innerWidth >= 992) {
      setActiveMega(megaKey || null);
    }
  };

  const handleMouseLeaveNav = () => {
    megaTimeoutRef.current = setTimeout(() => {
      setActiveMega(null);
    }, 200);
  };

  const handleMegaMouseEnter = () => {
    if (megaTimeoutRef.current) clearTimeout(megaTimeoutRef.current);
  };

  const toggleMobileSubmenu = (megaKey, e) => {
    if (window.innerWidth < 992) {
      e.preventDefault();
      setMobileSubmenu((prev) => (prev === megaKey ? null : megaKey));
    }
  };

  return (
    <nav
      className="navbar navbar-expand-lg sipl-navbar sticky-top"
      aria-label="Seikural Innovation main navigation"
    >
      <div className="container position-relative h-100 d-flex align-items-center justify-content-between">
        {/* ── Logo ── */}
        <Link
          to="/"
          className="navbar-brand sipl-navbar-brand py-0 me-auto me-lg-4 d-flex align-items-center text-decoration-none"
          aria-label="Seikural Innovations — home"
          onClick={closeMenu}
        >
          <span className="sipl-navbar-logo-wrapper">
            <img
              src={seikurallogo}
              alt="Seikural Innovations logo"
              decoding="async"
              className="sipl-navbar-logo d-inline-block align-middle img-fluid"
            />
          </span>
        </Link>

        {/* ── Hamburger Toggler ── */}
        <button
          className="navbar-toggler border-0 shadow-none"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#siplNavMenu"
          aria-controls="siplNavMenu"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon" />
        </button>

        {/* ── Collapsible Menu ── */}
        <div
          id="siplNavMenu"
          className="collapse navbar-collapse"
          ref={collapseRef}
        >
          <ul
            className="navbar-nav sipl-nav-list mx-auto mb-2 mb-lg-0 d-flex align-items-lg-center"
            onMouseLeave={handleMouseLeaveNav}
          >
            {NAV_ITEMS.map((item, idx) => {
              if (item.type === 'link') {
                return (
                  <li className="nav-item" key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `nav-link sipl-nav-link text-decoration-none ${isActive ? 'active' : ''}`
                      }
                      style={{ animationDelay: `${idx * 0.07}s` }}
                      onMouseEnter={(e) => handleMouseEnterLink(e, null)}
                      onClick={closeMenu}
                    >
                      {item.label}
                    </NavLink>
                  </li>
                );
              }

              // Mega Dropdown Link
              const isMegaOpen = activeMega === item.megaKey;
              const isMobileOpen = mobileSubmenu === item.megaKey;
              const isOpen = isMegaOpen || isMobileOpen;

              return (
                <li
                  className="nav-item dropdown position-static"
                  key={item.to}
                  onMouseEnter={(e) => handleMouseEnterLink(e, item.megaKey)}
                >
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `nav-link sipl-nav-link text-decoration-none d-inline-flex align-items-center ${
                        isActive || isOpen ? 'active' : ''
                      }`
                    }
                    style={{ animationDelay: `${idx * 0.07}s` }}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    title={`Explore ${item.label} options`}
                    onClick={(e) => {
                      if (window.innerWidth < 992) {
                        toggleMobileSubmenu(item.megaKey, e);
                      } else {
                        // Toggle dropdown on click without navigating
                        e.preventDefault();
                        if (megaTimeoutRef.current) clearTimeout(megaTimeoutRef.current);
                        setActiveMega((prev) => (prev === item.megaKey ? null : item.megaKey));
                      }
                    }}
                  >
                    <span>{item.label}</span>
                    <svg
                      className={`sipl-nav-chevron ${isOpen ? 'open' : ''}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </NavLink>

                  {/* Render Dedicated Component for Desktop */}
                  {isMegaOpen && item.megaKey === 'services' && (
                    <ServicesDropdown
                      closeMenu={closeMenu}
                      handleMouseEnter={handleMegaMouseEnter}
                      handleMouseLeave={handleMouseLeaveNav}
                    />
                  )}

                  {isMegaOpen && item.megaKey === 'solutions' && (
                    <SolutionsDropdown
                      closeMenu={closeMenu}
                      handleMouseEnter={handleMegaMouseEnter}
                      handleMouseLeave={handleMouseLeaveNav}
                    />
                  )}

                  {/* {isMegaOpen && item.megaKey === 'industries' && (
                    <IndustriesDropdown
                      closeMenu={closeMenu}
                      handleMouseEnter={handleMegaMouseEnter}
                      handleMouseLeave={handleMouseLeaveNav}
                    />
                  )} */}

                  {/* Mobile Submenu Accordion */}
                  {isMobileOpen && MOBILE_SUBMENU_ITEMS[item.megaKey] && (
                    <div className="d-lg-none ps-3 pe-2 py-2 my-1 rounded-3 bg-light border-start border-3 border-teal-500 sipl-mobile-submenu">
                      {MOBILE_SUBMENU_ITEMS[item.megaKey].map((sub) => (
                        <Link
                          key={sub.title}
                          to={sub.to}
                          className="d-block py-1 px-2 text-decoration-none text-secondary hover-teal rounded-2 small"
                          onClick={closeMenu}
                          style={{ fontSize: '0.825rem' }}
                        >
                          {sub.title}
                        </Link>
                      ))}
                      <Link
                        to={item.to}
                        className="d-block mt-2 pt-1 border-top fw-bold text-decoration-none small"
                        onClick={closeMenu}
                        style={{ fontSize: '0.8rem', color: '#0d9488' }}
                      >
                        Explore All {item.label} →
                      </Link>
                    </div>
                  )}
                </li>
              );
            })}


          </ul>

          {/* ── Contact CTA Button ── */}
          <div className="d-flex align-items-center">
            <Link
              to="/contact"
              className="sipl-cta-btn text-decoration-none"
              onClick={closeMenu}
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
      <ScrollProgressBar />
    </nav>
  );
};

export default Navbar;